import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Shield,
  Star,
  TrendingUp,
  Eye,
  MessageSquare,
  Search,
  CheckCircle,
  ArrowRight,
  Play,
  Bot,
  Zap,
  Award,
  Users,
  BarChart,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [consultationForm, setConsultationForm] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleQuickConsultation = async (e) => {
    e.preventDefault();
    if (!consultationForm.name || !consultationForm.email) {
      toast.error("Please fill in your name and email");
      return;
    }
    setIsLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: consultationForm.name,
        email: consultationForm.email,
        phone: consultationForm.phone,
        message: "Quick consultation request from homepage",
      });
      toast.success("Thank you! We'll contact you within 24 hours.");
      setConsultationForm({ name: "", email: "", phone: "" });
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const stats = [
    { value: "15+", label: "Years Experience", icon: Award },
    { value: "10K+", label: "Reviews Managed", icon: Star },
    { value: "500+", label: "Happy Clients", icon: Users },
    { value: "98%", label: "Success Rate", icon: TrendingUp },
  ];

  const platforms = [
    { name: "Google", icon: "G" },
    { name: "Yelp", icon: "Y" },
    { name: "Avvo", icon: "A" },
    { name: "Vitals", icon: "V" },
    { name: "WebMD", icon: "W" },
    { name: "Zillow", icon: "Z" },
    { name: "Facebook", icon: "F" },
    { name: "BBB", icon: "B" },
  ];

  const features = [
    {
      icon: Eye,
      title: "24/7 AI Monitoring",
      description: "Our AI agents continuously monitor your online presence across all major platforms.",
    },
    {
      icon: MessageSquare,
      title: "Smart Response System",
      description: "AI-powered response suggestions that maintain your brand voice while addressing concerns.",
    },
    {
      icon: TrendingUp,
      title: "Review Generation",
      description: "Automated campaigns to encourage satisfied customers to share their positive experiences.",
    },
    {
      icon: Shield,
      title: "Negative Content Suppression",
      description: "Strategic SEO and content promotion to push down negative search results.",
    },
    {
      icon: Search,
      title: "Search Result Optimization",
      description: "Turn every search about your business into a positive brand message.",
    },
    {
      icon: BarChart,
      title: "Analytics & Reporting",
      description: "Comprehensive dashboards showing your reputation score and improvement trends.",
    },
  ];

  const testimonials = [
    {
      name: "Dr. Sarah Mitchell",
      role: "Healthcare Provider",
      content: "5 Star Marketing transformed our online presence. Our Vitals and WebMD reviews improved dramatically, leading to 40% more patient inquiries.",
      platform: "Vitals & WebMD",
    },
    {
      name: "James Rodriguez",
      role: "Restaurant Owner",
      content: "The AI monitoring caught a fake review campaign against us immediately. They handled everything professionally and our Yelp rating recovered in weeks.",
      platform: "Yelp & Google",
    },
    {
      name: "Attorney Michael Chen",
      role: "Law Firm Partner",
      content: "Our Avvo profile went from average to exceptional. The reputation management team understands the legal industry perfectly.",
      platform: "Avvo",
    },
  ];

  return (
    <div data-testid="home-page" className="pt-20">
      {/* Hero Section */}
      <section className="hero-gradient relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium">
                <Bot className="h-4 w-4" />
                AI-Powered Reputation Management
              </div>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-foreground leading-tight"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                We Make You{" "}
                <span className="gradient-text">Look Good Online</span>
              </h1>
              <p className="text-lg text-muted-foreground max-w-xl">
                Your online reputation matters. Our AI agents monitor, protect, and enhance 
                your presence across Google, Yelp, Avvo, Vitals, WebMD, Zillow, and more.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button
                    data-testid="hero-consultation-btn"
                    size="lg"
                    className="bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Free Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button
                    data-testid="hero-services-btn"
                    size="lg"
                    variant="outline"
                    className="rounded-full px-8 py-6 text-lg font-medium border-2"
                  >
                    Our Services
                  </Button>
                </Link>
              </div>
              
              {/* Trust badges */}
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Award className="h-5 w-5 text-[#F37021]" />
                  <span className="text-sm font-medium">Google Platinum Partner</span>
                </div>
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-[#F37021]" />
                  <span className="text-sm font-medium">BBB A+ Rated</span>
                </div>
              </div>
            </div>

            {/* Quick Consultation Form */}
            <Card className="bg-card border-border shadow-2xl">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Get a Free Consultation
                </h3>
                <p className="text-muted-foreground mb-6">
                  Find out how we can protect your reputation
                </p>
                <form onSubmit={handleQuickConsultation} className="space-y-4">
                  <Input
                    placeholder="Your Name *"
                    value={consultationForm.name}
                    onChange={(e) => setConsultationForm({ ...consultationForm, name: e.target.value })}
                    data-testid="hero-name-input"
                    className="h-12"
                    required
                  />
                  <Input
                    type="email"
                    placeholder="Email Address *"
                    value={consultationForm.email}
                    onChange={(e) => setConsultationForm({ ...consultationForm, email: e.target.value })}
                    data-testid="hero-email-input"
                    className="h-12"
                    required
                  />
                  <Input
                    type="tel"
                    placeholder="Phone Number"
                    value={consultationForm.phone}
                    onChange={(e) => setConsultationForm({ ...consultationForm, phone: e.target.value })}
                    data-testid="hero-phone-input"
                    className="h-12"
                  />
                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="hero-submit-btn"
                    className="w-full bg-[#F37021] hover:bg-[#D95F18] text-white h-12 text-lg font-semibold rounded-full"
                  >
                    {isLoading ? "Submitting..." : "Get Free Consultation"}
                  </Button>
                </form>
                <p className="text-xs text-muted-foreground text-center mt-4">
                  We respond within 24 hours. Your information is confidential.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Platforms We Monitor */}
      <section className="py-12 bg-muted/50 border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-center text-sm text-muted-foreground mb-8">
            PLATFORMS WE MONITOR & MANAGE
          </p>
          <div className="flex justify-center items-center gap-8 lg:gap-16 flex-wrap">
            {platforms.map((platform) => (
              <div key={platform.name} className="flex items-center gap-2 text-muted-foreground hover:text-[#F37021] transition-colors">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center font-bold text-sm">
                  {platform.icon}
                </div>
                <span className="font-medium">{platform.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                data-testid={`stat-${index}`}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F37021]/10 rounded-2xl mb-4">
                  <stat.icon className="h-8 w-8 text-[#F37021]" />
                </div>
                <p
                  className="text-4xl lg:text-5xl font-extrabold text-foreground mb-2"
                  style={{ fontFamily: "Outfit, sans-serif" }}
                >
                  {stat.value}
                </p>
                <p className="text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Advantage Section */}
      <section className="py-20 lg:py-28 section-muted">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#F37021]/10 text-[#F37021] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="h-4 w-4" />
                Our AI Advantage
              </div>
              <h2
                className="text-3xl sm:text-4xl font-bold text-foreground mb-6"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                AI Agents That Never Sleep
              </h2>
              <p className="text-lg text-muted-foreground mb-6">
                While other reputation companies rely on manual monitoring, our AI agents work 
                24/7 to protect your online presence. Instant alerts, smart responses, and 
                proactive reputation building.
              </p>
              <ul className="space-y-4">
                {[
                  "Real-time review monitoring across 50+ platforms",
                  "AI-powered sentiment analysis and alert system",
                  "Automated positive review request campaigns",
                  "Smart response suggestions maintaining your voice",
                  "Predictive analytics to prevent reputation issues",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-[#F37021] flex-shrink-0" />
                    <span className="text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/services" className="inline-block mt-8">
                <Button
                  data-testid="ai-learn-more-btn"
                  className="bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full px-8"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-[#F37021]/20 to-[#F37021]/5 rounded-2xl p-8 lg:p-12">
                <div className="space-y-6">
                  {[
                    { platform: "Google Reviews", status: "Monitoring", reviews: "↑ 4.8" },
                    { platform: "Yelp", status: "New Review Detected", reviews: "Response Needed" },
                    { platform: "Avvo", status: "All Clear", reviews: "5.0 Rating" },
                  ].map((item, index) => (
                    <div key={index} className="bg-card rounded-xl p-4 flex items-center justify-between shadow-sm">
                      <div>
                        <p className="font-semibold text-foreground">{item.platform}</p>
                        <p className="text-sm text-muted-foreground">{item.status}</p>
                      </div>
                      <span className={`text-sm font-medium px-3 py-1 rounded-full ${
                        item.status === "New Review Detected" 
                          ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" 
                          : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                      }`}>
                        {item.reviews}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-[#F37021]/10 rounded-xl">
                  <div className="flex items-center gap-3">
                    <Bot className="h-8 w-8 text-[#F37021]" />
                    <div>
                      <p className="font-semibold text-foreground">AI Agent Active</p>
                      <p className="text-sm text-muted-foreground">Monitoring 8 platforms in real-time</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 lg:py-28 section-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Complete Reputation{" "}
              <span className="gradient-text">Management</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Everything you need to protect, monitor, and improve your online reputation.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card
                key={feature.title}
                data-testid={`feature-card-${index}`}
                className="card-hover bg-card border-border hover:border-[#F37021]/30 transition-all duration-300"
              >
                <CardContent className="p-8">
                  <div className="feature-icon w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                    <feature.icon className="h-7 w-7" />
                  </div>
                  <h3
                    className="text-xl font-bold text-foreground mb-3"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-28 section-muted">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              Client <span className="gradient-text">Success Stories</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See how we've helped businesses and professionals protect their reputation.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card
                key={testimonial.name}
                data-testid={`testimonial-${index}`}
                className="testimonial-card bg-card border-border"
              >
                <CardContent className="p-8">
                  <div className="flex items-center gap-1 mb-4">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="h-5 w-5 fill-[#F37021] text-[#F37021]" />
                    ))}
                  </div>
                  <p className="text-foreground mb-6">"{testimonial.content}"</p>
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F37021] to-[#FF9B5C] flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{testimonial.name}</p>
                      <p className="text-sm text-muted-foreground">{testimonial.role}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <span className="text-xs text-[#F37021] font-medium">{testimonial.platform}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#1A1A1A] relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center relative z-10">
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Ready to Protect Your Reputation?
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-10">
            Get a free reputation analysis and see how we can help improve your online presence.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button
                data-testid="cta-consultation-btn"
                size="lg"
                className="bg-[#F37021] hover:bg-[#D95F18] text-white rounded-full px-10 py-6 text-lg font-semibold"
              >
                Get Free Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button
                data-testid="cta-pricing-btn"
                size="lg"
                variant="outline"
                className="rounded-full px-10 py-6 text-lg font-medium text-white border-white/30 hover:bg-white/10"
              >
                View Pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
